// SampleRequestDetail — port of sampler/src/views/SampleRequestDetail.vue +
// BriefPanel + ProgressSteps + ReferenceCard + CommentsThread + ActivityLog.
// admin = customer (edits), supply = factory (own org, confirms/ships). The
// brief auto-saves (debounced); the sticky panel is phase-aware.

import { useEffect, useRef, useState } from 'react'
import { useParams, useNavigate } from '@tanstack/react-router'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import {
  ChevronLeft, Printer, Check, Trash2, Star, ExternalLink, Download,
} from 'lucide-react'
import { Card } from '../../components/ui/card'
import { PrioritySelector } from './PrioritySelector'
import {
  PHASES, phaseIndex, formatSampleCode,
  type Brief, type SampleReference,
} from './lib'
import {
  getSample, getBrief, updateBrief, listReferences, addReference, removeReference,
  setMainReference, setPriority, getActivity, markRead, listComments, exportSampleDetail, printSampleDetail,
} from './api'
import { ShareButton } from './ShareButton'
import { BriefEditor, CommentsThread, ActivityLog } from './SampleDetailParts'
import { SampleAdoptCard } from './SampleAdoptCard'
import { LinkedTasksPanel } from '../staff-tasks/LinkedTasksPanel'
import { useT } from '../../i18n'
import { sampleRequestDetailI18n } from '../../i18n/samples/request-detail'
import { samplesCommonI18n } from '../../i18n/samples/common'

function ProgressSteps({ current }: { current: string }) {
  const active = phaseIndex(current)
  const sc = useT(samplesCommonI18n)
  return (
    <ol className="flex w-full items-center gap-1 text-xs">
      {PHASES.map((p, i) => (
        <li key={p.key} className={`flex flex-1 items-center gap-2 ${i === active ? 'font-semibold text-[var(--color-info)]' : 'text-[var(--color-text-muted)]'}`}>
          <span className={`grid h-6 w-6 place-items-center rounded-full border text-[10px] ${i < active ? 'border-indigo-600 bg-indigo-600 text-white' : i === active ? 'border-[var(--color-border)] bg-[var(--color-surface-muted)] text-[var(--color-info)]' : 'border-[var(--color-border)] bg-[var(--color-surface)] text-[var(--color-text-muted)]'}`}>
            {i < active ? <Check size={12} /> : i + 1}
          </span>
          <span className="truncate">{sc.phases[p.key]}</span>
          {i < PHASES.length - 1 && <span className={`ml-1 hidden h-px flex-1 sm:block ${i < active ? 'bg-indigo-300' : 'bg-[var(--color-border)]'}`} />}
        </li>
      ))}
    </ol>
  )
}

function photoUrl(p: { url: string } | string): string {
  return typeof p === 'string' ? p : p.url
}

function ReferenceCard({
  reference, isMain, canEdit, hideExternal, onUsePhoto, onRemove, onSetMain,
}: {
  reference: SampleReference
  isMain?: boolean
  canEdit: boolean
  hideExternal: boolean
  onUsePhoto: (url: string) => void
  onRemove: (id: string) => void
  onSetMain: (id: string) => void
}) {
  const t = useT(sampleRequestDetailI18n)
  const photos = reference.photos || []
  return (
    <div className={`rounded-lg border p-3 ${isMain ? 'border-amber-300 bg-amber-50/40' : 'border-[var(--color-border)]'}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="flex items-center gap-1.5">
            {isMain && <Star size={12} className="text-amber-500 shrink-0" fill="currentColor" />}
            <span className="truncate text-sm font-medium text-[var(--color-text)]">{reference.title || t.reference}</span>
          </div>
          {!hideExternal && (
            <div className="mt-0.5 flex flex-wrap items-center gap-x-2 text-[11px] text-[var(--color-text-muted)]">
              {reference.price && <span>{reference.price}</span>}
              {reference.seller && <span>· {reference.seller}</span>}
              {reference.url && (
                <a href={reference.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-0.5 text-indigo-600 hover:underline">
                  <ExternalLink size={10} /> {t.listing}
                </a>
              )}
            </div>
          )}
        </div>
        {canEdit && (
          <div className="flex shrink-0 items-center gap-0.5">
            {!isMain && (
              <button title={t.setAsMain} className="grid h-7 w-7 place-items-center rounded text-[var(--color-text-muted)] hover:bg-amber-50 hover:text-amber-600" onClick={() => onSetMain(reference.id)}>
                <Star size={13} />
              </button>
            )}
            <button title={t.remove} className="grid h-7 w-7 place-items-center rounded text-[var(--color-text-muted)] hover:bg-rose-50 hover:text-rose-600" onClick={() => onRemove(reference.id)}>
              <Trash2 size={13} />
            </button>
          </div>
        )}
      </div>
      {photos.length > 0 && (
        <div className="mt-2 grid grid-cols-3 gap-1.5 sm:grid-cols-4">
          {photos.map((p, i) => {
            const u = photoUrl(p)
            return (
              <div key={`${u}-${i}`} className="group relative aspect-square overflow-hidden rounded border border-[var(--color-border)] bg-[var(--color-surface-muted)]">
                <img src={u} className="h-full w-full object-cover" alt="" loading="lazy" />
                {canEdit && (
                  <button title={t.useInBrief} className="absolute inset-x-0 bottom-0 bg-slate-900/70 py-0.5 text-[10px] font-medium text-white opacity-0 transition group-hover:opacity-100" onClick={() => onUsePhoto(u)}>
                    {t.use}
                  </button>
                )}
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

export function SampleRequestDetail({ isFactory, basePath }: { isFactory: boolean; basePath: string }) {
  const { id } = useParams({ strict: false }) as { id: string }
  const navigate = useNavigate()
  const qc = useQueryClient()
  const t = useT(sampleRequestDetailI18n)
  const sc = useT(samplesCommonI18n)

  const sampleQ = useQuery({ queryKey: ['sample', id], queryFn: () => getSample(id), enabled: !!id })
  const briefQ = useQuery({ queryKey: ['sample-brief', id], queryFn: () => getBrief(id), enabled: !!id })
  const refsQ = useQuery({ queryKey: ['sample-refs', id], queryFn: () => listReferences(id), enabled: !!id })
  const commentsQ = useQuery({ queryKey: ['sample-comments', id], queryFn: () => listComments(id), enabled: !!id })
  const activityQ = useQuery({ queryKey: ['sample-activity', id], queryFn: () => getActivity(id), enabled: !!id })

  const sample = sampleQ.data
  const [brief, setBrief] = useState<Brief | null>(null)
  const [saving, setSaving] = useState(false)
  const [exportingPdf, setExportingPdf] = useState(false)
  const [printing, setPrinting] = useState(false)
  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const pendingSave = useRef<(() => void) | null>(null)
  const mountedRef = useRef(true)

  // Sync the local brief whenever a (new) server brief arrives
  // (adjust-during-render with a previous-value comparison).
  const [prevBriefData, setPrevBriefData] = useState<Brief | null>(null)
  if (briefQ.data && briefQ.data !== prevBriefData) {
    setPrevBriefData(briefQ.data)
    setBrief(briefQ.data)
  }
  // Mark read on open (clears the unread dot in the list).
  useEffect(() => { if (id && sample && sample.status !== 'draft') void markRead(id).then(() => qc.invalidateQueries({ queryKey: ['samples'] })) }, [id, sample, qc])

  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
      if (saveTimer.current) clearTimeout(saveTimer.current)
      if (pendingSave.current) pendingSave.current()
    }
  }, [])

  function invalidateAll() {
    void qc.invalidateQueries({ queryKey: ['sample', id] })
    void qc.invalidateQueries({ queryKey: ['sample-activity', id] })
    void qc.invalidateQueries({ queryKey: ['samples'] })
  }

  const phase = sample?.status ?? 'draft'
  const locked = !!sample?.locked
  const refs = refsQ.data ?? []
  const mainRef = refs[0]
  const otherRefs = refs.slice(1)

  function queueSave(next: Brief) {
    setBrief(next)
    if (saveTimer.current) clearTimeout(saveTimer.current)
    const run = async () => {
      pendingSave.current = null
      if (mountedRef.current) setSaving(true)
      try {
        await updateBrief(id, next)
        invalidateAll()
      } finally {
        if (mountedRef.current) setSaving(false)
      }
    }
    pendingSave.current = () => { void run() }
    saveTimer.current = setTimeout(run, 600)
  }

  if (sampleQ.isLoading || !sample) {
    return <div className="p-8 text-center text-sm text-[var(--color-text-muted)]">{t.loading}</div>
  }

  return (
    <div className="space-y-5">
      <div className="flex items-start justify-between gap-3">
        <button onClick={() => navigate({ to: basePath })} className="-ml-1 inline-flex items-center gap-1 text-sm text-[var(--color-text-muted)] hover:text-[var(--color-text)]">
          <ChevronLeft size={16} /> {t.back}
        </button>
        <div className="flex items-center gap-2">
          <button
            onClick={async () => {
              setExportingPdf(true)
              try {
                await exportSampleDetail(id, formatSampleCode(sample.pipeline_number))
              } finally {
                setExportingPdf(false)
              }
            }}
            disabled={exportingPdf}
            className="text-xs px-3 py-1.5 border border-[var(--color-border-strong)] rounded inline-flex items-center gap-1 hover:bg-[var(--color-surface-muted)] disabled:opacity-50"
          >
            <Download size={14} /> <span className="hidden sm:inline">{exportingPdf ? t.exporting : t.exportPdf}</span>
          </button>
          <ShareButton id={id} label={`${sample.title || t.sample} (${formatSampleCode(sample.pipeline_number)})`} />
          <button
            onClick={async () => {
              setPrinting(true)
              try {
                await printSampleDetail(id)
              } finally {
                setPrinting(false)
              }
            }}
            disabled={printing}
            className="text-xs px-3 py-1.5 border border-[var(--color-border-strong)] rounded inline-flex items-center gap-1 hover:bg-[var(--color-surface-muted)] disabled:opacity-50"
          >
            <Printer size={14} /> <span className="hidden sm:inline">{printing ? t.preparing : t.print}</span>
          </button>
        </div>
      </div>

      <Card className="flex flex-col gap-4 p-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="truncate text-xl font-semibold text-[var(--color-text)]">{sample.title || t.sampleRequest}</h1>
            <span className="rounded-full bg-[var(--color-surface-muted)] px-2 py-0.5 font-mono text-xs text-[var(--color-text)]">{formatSampleCode(sample.pipeline_number) || '—'}</span>
            <PrioritySelector value={sample.priority} editable={!isFactory} size="sm" onChange={async (v) => { await setPriority(id, v); invalidateAll() }} />
          </div>
          <p className="mt-1 text-xs text-[var(--color-text-muted)]">{t.status}<span className="font-medium text-[var(--color-text)]">{sc.phaseLabel(phase)}</span></p>
        </div>
        <div className="w-full sm:w-2/3"><ProgressSteps current={phase} /></div>
      </Card>

      {!isFactory && phase === 'received' && (
        <SampleAdoptCard sample={sample} onChanged={invalidateAll} />
      )}

      <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
        {/* References */}
        <Card className="p-4">
          <div className="mb-3 flex items-center justify-between">
            <h2 className="text-lg font-semibold text-[var(--color-text)]">
              {t.referenceListings} <span className="ml-1 text-sm font-medium text-[var(--color-text-muted)]">({refs.length}/21)</span>
            </h2>
          </div>
          {!isFactory && <RefAdder id={id} disabled={locked} onAdded={() => refsQ.refetch()} />}
          {refs.length > 0 ? (
            <div className="space-y-4">
              {mainRef && (
                <ReferenceCard reference={mainRef} isMain canEdit={!isFactory && !locked} hideExternal={isFactory}
                  onUsePhoto={(u) => brief && queueSave({ ...brief, photos: [...brief.photos, { url: u }] })}
                  onRemove={async (rid) => { await removeReference(id, rid); refsQ.refetch() }}
                  onSetMain={async (rid) => { await setMainReference(id, rid); refsQ.refetch() }} />
              )}
              {otherRefs.length > 0 && (
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  {otherRefs.map((r) => (
                    <ReferenceCard key={r.id} reference={r} canEdit={!isFactory && !locked} hideExternal={isFactory}
                      onUsePhoto={(u) => brief && queueSave({ ...brief, photos: [...brief.photos, { url: u }] })}
                      onRemove={async (rid) => { await removeReference(id, rid); refsQ.refetch() }}
                      onSetMain={async (rid) => { await setMainReference(id, rid); refsQ.refetch() }} />
                  ))}
                </div>
              )}
            </div>
          ) : (
            <div className="rounded-lg border border-dashed border-[var(--color-border)] p-8 text-center text-sm text-[var(--color-text-muted)]">
              {t.noReferences}{!isFactory && t.pasteEtsyHint}
            </div>
          )}
        </Card>

        {/* Brief */}
        <Card className="p-4">
          <BriefEditor
            id={id} brief={brief} setBrief={queueSave} saving={saving} locked={locked}
            isFactory={isFactory} sample={sample} basePath={basePath} onPhaseChange={() => { invalidateAll(); void briefQ.refetch() }}
          />
        </Card>
      </div>

      <LinkedTasksPanel
        shell={isFactory ? 'supply' : 'admin'}
        link={{
          kind: 'sample',
          id,
          label: `${formatSampleCode(sample.pipeline_number) || 'Sample'} ${sample.title ?? ''}`.trim().slice(0, 200),
        }}
      />
      <CommentsThread id={id} comments={commentsQ.data ?? []} isFactory={isFactory} onChange={() => { commentsQ.refetch(); qc.invalidateQueries({ queryKey: ['samples'] }) }} />
      <ActivityLog activity={activityQ.data ?? []} />
    </div>
  )
}

function RefAdder({ id, disabled, onAdded }: { id: string; disabled: boolean; onAdded: () => void }) {
  const t = useT(sampleRequestDetailI18n)
  const [url, setUrl] = useState('')
  const [busy, setBusy] = useState(false)
  return (
    <form
      className="mb-4 flex gap-2"
      onSubmit={async (e) => {
        e.preventDefault()
        if (!url.trim()) return
        setBusy(true)
        try {
          await addReference(id, url.trim())
          setUrl('')
          onAdded()
        } finally {
          setBusy(false)
        }
      }}
    >
      <input value={url} onChange={(e) => setUrl(e.target.value)} type="url" placeholder="https://www.etsy.com/listing/…" disabled={disabled || busy}
        className="flex-1 px-3 py-2 text-sm border border-[var(--color-border-strong)] rounded focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:bg-[var(--color-surface-muted)]" />
      <button type="submit" disabled={!url || disabled || busy} className="whitespace-nowrap text-xs px-3 py-2 border border-[var(--color-border-strong)] rounded hover:bg-[var(--color-surface-muted)] disabled:opacity-50">
        {busy ? t.adding : t.add}
      </button>
    </form>
  )
}
